import { forwardRef, isValidElement, cloneElement } from "react";
import { Button } from "app/components/ui/Button/Button";
import { useSortContext } from "../../contexts/SortProvider";
import classNames from "classnames";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { FormattedMessage } from "react-intl";
import { ArrowUpDownIcon } from "../../icons/ArrowUpDownIcon";
import "./SortTrigger.scss";
import PropTypes from "prop-types";

const composeHandlers = (userHandler, ourHandler) => {
	return function handleEvent(e) {
		if (typeof userHandler === "function") {
			userHandler(e);
		}

		if (e?.defaultPrevented) {
			return;
		}

		if (typeof e.isPropagationStopped === "function" && e.isPropagationStopped()) {
			return;
		}

		if (typeof ourHandler === "function") {
			ourHandler(e);
		}
	};
};

export const SortTrigger = forwardRef(function SortTrigger(
	{ asChild = false, variant = "borderless", className, children, onClick, ...props },
	ref
) {
	const { openDrawer, isOpen } = useSortContext();

	const sortTriggerClassName = classNames(
		"sort-trigger",
		{ "sort-trigger--borderless": variant === "borderless" },
		className
	);

	const handleClick = composeHandlers(onClick, openDrawer);

	const aria = { "aria-haspopup": "dialog", "aria-expanded": isOpen };

	if (asChild) {
		if (!isValidElement(children)) {
			return null;
		}

		return cloneElement(children, {
			className: classNames(children.props.className, sortTriggerClassName),
			onClick: composeHandlers(children.props.onClick, handleClick),
			ref,
			...props,
			...aria,
		});
	}

	return (
		<Button
			variant={variant}
			type="button"
			className={sortTriggerClassName}
			onClick={handleClick}
			startIcon={<ArrowUpDownIcon />}
			ref={ref}
			{...props}
			{...aria}
		>
			{children ?? (
				<Typography
					variant={TYPOGRAPHY_VARIANTS.REGULAR}
					isBold
					component="span"
					className="sort-trigger__label"
				>
					<FormattedMessage id="sort.sort" />
				</Typography>
			)}
		</Button>
	);
});

SortTrigger.propTypes = {
	asChild: PropTypes.bool,
	variant: PropTypes.oneOf(["primary", "secondary", "borderless"]),
	onClick: PropTypes.func,
};
